import { DsMessageService } from './shared/services/ds-message.service';
import { DSMessage } from './models/api/ds-message';
import { NotificationsService } from './notifications/notifications.service';
import { SortData } from './models/sort.model';
import { FilterInputService } from './vehicles/filter-input/filter-input.service';
import { Component, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { ViewEncapsulation } from '@angular/core';
import { AdminPanelService } from './admin-panel/admin-panel.service';
import { PermissionsService } from './core/permissions.service';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from './core/auth.service';
import { AppStore } from './core/state/app-store';
// import { updateGoogleAnalyticsPath } from '../gtag';
import { UserService } from './core/user.service';
import { UserVehicleGroupOption } from './models/vehicle-types.model';
import { version } from '../../package.json';
import { DSNotification } from './models/notification';
import { Subscription } from 'rxjs';
import { UserIdleService } from './services/user-idle-service/user-idle.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [FilterInputService, NotificationsService],
  encapsulation: ViewEncapsulation.None,
})
export class AppComponent implements OnInit, OnDestroy {
  public showTopBar = false;
  public paymentDialog = {
    visible: false,
    applicationAccess: 'unlimited',
  };
  public showEmailDialog: boolean = false;
  private openAdminPanel = false;
  private isAdmin = false;
  public lastMessage: DSMessage;
  @ViewChild('adminSidenav') adminSidenav;
  version: string;
  notifications: DSNotification[];
  unreadedNotificationsCount: number;
  subscription: Subscription = new Subscription();

  constructor(
    public adminPanelService: AdminPanelService,
    public permissionsService: PermissionsService,
    public activatedRoute: ActivatedRoute,
    public userService: UserService,
    private translate: TranslateService,
    public authService: AuthService,
    private appStore: AppStore,
    private router: Router,
    private filterInputService: FilterInputService,
    private notificationsService: NotificationsService,
    private dsMessageService: DsMessageService,
    private userIdleService: UserIdleService
  ) {
    this.version = version;
    (document as any).appVersion = version;
    const languages = ['pl', 'en'];
    translate.addLangs(languages);

    const defaultLang = this.authService.getCookie('locale')
      ? this.authService.getCookie('locale')
      : languages[0];
    translate.setDefaultLang(defaultLang);
    const now = new Date();
    now.setMonth(now.getMonth() + 3);
    document.cookie =
      'locale=' + defaultLang + ';path=/;expires=' + now.toUTCString();
    translate.use(defaultLang);
    const selectedMap = localStorage.getItem('selectedMap');
    if (selectedMap === 'here') {
      localStorage.setItem('selectedMap', 'OSM');
    }
  }

  get isLoginPage(): boolean {
    return this.router.url.includes('login');
  }

  ngOnInit() {
    this.adminPanelService.initAdminPanel(this.adminSidenav);

    this.authService.authenticated.subscribe((authenticated) => {
      if (authenticated) {
        this.appStore.initApp();
        if (this.authService.isDSLocateUser()) {
          this.loadDSLocateUserInitialData();
        } else {
          this.loadInitialData();
        }
        this.loadLastMessage();
      }
    });

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        // updateGoogleAnalyticsPath(this.router.url);

        if (this.router.url.match(/^\/login/gm)) {
          this.showTopBar = false;
          this.adminPanelService.closeAdminPanel();
        } else {
          this.showTopBar = true;

          this.openAdminPanel =
            this.activatedRoute.snapshot.queryParams['adminPanel'];
          this.checkForOpeningAdminPanel();
        }
      }
    });
    this.toggleAdminPanelVisibility();
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  loadLastMessage() {
    this.dsMessageService
      .loadLastMessage()
      .subscribe((lastMessage: DSMessage) => {
        this.lastMessage = lastMessage;
      });
  }

  loadDSLocateUserInitialData() {
    this.isAdmin = null;
    this.appStore.refreshDevicesInformation().subscribe();
    this.appStore.subscribeToDSLocateDevices();

    this.userService.getVehiclesFilter().subscribe((filters: any) => {
      this.appStore.setFilterStateValue({
        ...this.appStore.getState().filterVehicles,
        text: filters.text,
        group: null,
        type: filters.type !== '0' ? filters.type : undefined,
        pattern: new RegExp(filters.text, 'i'),
        status: filters.status,
        showFilter: filters.showFilter,
      });
    });

    // this.userService.getVehiclesSort().subscribe((sortData: SortData) => {
    //   this.appStore.setSortStateValue(sortData);
    // });
    this.notificationsService
      .loadLastPerVehicleNotification()
      .subscribe((notifications) => {});

    this.notificationsService
      .getUserNotifications()
      .subscribe((userNotifications) => {
        this.notifications = userNotifications.notifications;
        this.unreadedNotificationsCount = userNotifications.unread;
      });
  }

  private filterGroupMap(filters, groups) {
    let selectedGroup = [];

    if (filters.group) {
      selectedGroup = groups
        .filter((group) => {
          return filters.group.map(Number).includes(group.value.id);
        })
        .map((res) => res.value);
    }

    return selectedGroup;
  }

  loadInitialData() {
    this.permissionsService
      .refreshUserPermissions()
      .subscribe((permissions) => {
        if (permissions && permissions.length > 0) {
          this.isAdmin = permissions.indexOf('www_menu_admin') !== -1;
          this.checkForOpeningAdminPanel();
          if (permissions.indexOf('www_poi_importer') !== -1) {
            this.appStore.storePoisCategories().subscribe();
            this.appStore.storePois().subscribe();
          }
        }
      });
    this.userService.loadFleetTranslations().subscribe((translations) => {
      this.translate.setTranslation(
        this.translate.currentLang,
        translations,
        true
      );
    });
    this.appStore.storeGetPharmacyList().subscribe();
    this.appStore.storeVehicles().subscribe();
    this.appStore.storeColumns().subscribe();
    this.appStore.storeDisplaySettings().subscribe();
    this.appStore.pollForVehiclesWithPositions();
    this.userService.getUserInfo().subscribe((resp) => {
      this.paymentDialog.applicationAccess = resp.applicationAccess;
      this.paymentDialog.visible = resp.applicationAccess !== 'unlimited';
      this.showEmailDialog = !this.paymentDialog.visible && resp.email === '';
    });
    this.userService.getVehiclesFilter().subscribe((filters: any) => {
      this.filterInputService
        .getUserGroups()
        .subscribe((groups: UserVehicleGroupOption[]) => {
          let selectedGroup = this.filterGroupMap(filters, groups);

          this.appStore.setFilterStateValue({
            ...this.appStore.getState().filterVehicles,
            text: filters.text,
            group: selectedGroup ? selectedGroup : [],
            type: filters.type !== '0' ? filters.type : undefined,
            pattern: new RegExp(filters.text, 'i'),
            status: filters.status,
            showFilter: filters.showFilter,
          });
        });
    });
    // this.userService.getVehiclesSort().subscribe((sortData: SortData) => {
    //   this.appStore.setSortStateValue(sortData);
    // });
    this.notificationsService
      .loadLastPerVehicleNotification()
      .subscribe((notifications) => {});

    this.notificationsService
      .getUserNotifications()
      .subscribe((userNotifications) => {
        this.notifications = userNotifications.notifications;
        this.unreadedNotificationsCount = userNotifications.unread;
      });
  }

  checkForOpeningAdminPanel() {
    if (this.isAdmin && this.openAdminPanel) {
      this.adminPanelService.openAdminPanel();
    }
  }

  private toggleAdminPanelVisibility(): void {
    this.subscription =
      this.adminPanelService.toggleAdminPanelVisibility.subscribe((open) => {
        if (open) {
          this.adminPanelService.openAdminPanel();
        } else {
          this.adminPanelService.closeAdminPanel();
        }
      });
  }
}
